import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, Plus, Trash2, Eye, X, PauseCircle, PlayCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { alertasApi, errorMsg } from './alertasObra.api';
import { getAuthClaims } from '../../../utils/authToken';
import R from '../weather/clima/reglasClima';

/* 🏗️ Alertas de la obra: primero QUÉ avisos (tarjetas con su switch, sin pedir datos), después
   ¿A QUIÉN? (WhatsApp / mail) y recién al guardar se valida. La selección se aplica a todos los números de
   esta cámara (el backend guarda una copia por número en alertConfigs, igual que siempre).
   "Cámara sin imágenes" vive en 📷 Cámara y las entregas en 🚚 Entregas.
   Con modo="clima" muestra los avisos de clima (obra.alertas.clima) con los mismos destinatarios: lo usan
   🌦️ Clima en Inteligencia › Alertas y Obra › Clima › Alertas. */

const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DIAS_LARGO = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];


const ESTADO_PREVIA = {
  avisaria: { txt: 'Hoy avisaría', cls: 'text-amber-800 bg-amber-50 border-amber-200' },
  ok: { txt: 'Todo en orden', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  sin_datos: { txt: 'Faltan datos', cls: 'text-gray-600 bg-gray-50 border-gray-200' },
  apagada: { txt: 'Apagado', cls: 'text-gray-500 bg-gray-50 border-gray-200' },
};

export const formatearTelefono = (p) => {
  const s = String(p || '');
  if (/^549\d{10}$/.test(s)) return `+54 9 ${s.slice(3, 5)} ${s.slice(5, 9)}-${s.slice(9)}`;
  return s ? `+${s}` : '';
};
const soloDigitos = (s) => String(s || '').replace(/\D/g, '');
const copia = (o) => JSON.parse(JSON.stringify(o));

// ---------- piezas chicas ----------
function Switch({ on, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors shrink-0 ${on ? 'bg-blue-600' : 'bg-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${on ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}



function Dias({ activos, onToggle, disabled }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {DIAS.map((d, k) => {
        const on = activos.includes(k + 1);
        return (
          <button key={k} type="button" disabled={disabled} title={DIAS_LARGO[k]} aria-label={DIAS_LARGO[k]} aria-pressed={on} onClick={() => onToggle(k + 1)}
            className={`w-9 h-9 rounded-full text-sm font-semibold border transition-colors ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            {d}
          </button>
        );
      })}
    </div>
  );
}

const inputCls = 'border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400';

function Hora({ value, onChange, disabled, label }) {
  return <input type="time" aria-label={label} value={value} disabled={disabled} onChange={(e) => e.target.value && onChange(e.target.value)} className={`${inputCls} w-[6.5rem]`} />;
}

// ---------- horario de trabajo (mismos 3 modos que la página del bot) ----------
function Horario({ obra, set }) {
  const H = obra.horarios;
  const modo = obra.horarioModo || 'uniforme';
  const activos = [1, 2, 3, 4, 5, 6, 7].filter((d) => H[d]?.activo);
  const ref = H[activos[0] || 1];
  const cambiar = (dias, campo, v) => set((o) => { dias.forEach((d) => { o.horarios[d][campo] = v; }); });
  const MODOS = [['uniforme', 'Todos los días igual'], ['semana_finde', 'Semana y fin de semana'], ['diario', 'Día por día']];

  let cuerpo;
  if (modo === 'uniforme') {
    cuerpo = (
      <div className="space-y-3 pt-2">
        <div>
          <p className="text-xs text-gray-500 mb-1.5">Días que se trabaja</p>
          <Dias activos={activos} onToggle={(d) => set((o) => { o.horarios[d].activo = !o.horarios[d].activo; })} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-700 w-20">Horario</span>
          <Hora label="Entrada" value={ref.inicio} onChange={(v) => cambiar([1, 2, 3, 4, 5, 6, 7], 'inicio', v)} />
          <span className="text-gray-400">a</span>
          <Hora label="Salida" value={ref.fin} onChange={(v) => cambiar([1, 2, 3, 4, 5, 6, 7], 'fin', v)} />
        </div>
      </div>
    );
  } else if (modo === 'semana_finde') {
    const grupos = [['Lunes a viernes', [1, 2, 3, 4, 5]], ['Sábado y domingo', [6, 7]]];
    cuerpo = (
      <div className="divide-y divide-gray-100">
        {grupos.map(([nombre, dias]) => {
          const on = dias.some((d) => H[d].activo);
          return (
            <div key={nombre} className="flex items-center gap-3 py-2.5 flex-wrap">
              <Switch on={on} label={nombre} onChange={(v) => set((o) => { dias.forEach((d) => { o.horarios[d].activo = v; }); })} />
              <span className={`text-sm w-32 ${on ? 'text-gray-800' : 'text-gray-400'}`}>{nombre}</span>
              <div className="flex items-center gap-2">
                <Hora label={`Entrada ${nombre}`} disabled={!on} value={H[dias[0]].inicio} onChange={(v) => cambiar(dias, 'inicio', v)} />
                <span className="text-gray-400">a</span>
                <Hora label={`Salida ${nombre}`} disabled={!on} value={H[dias[0]].fin} onChange={(v) => cambiar(dias, 'fin', v)} />
              </div>
            </div>
          );
        })}
      </div>
    );
  } else {
    cuerpo = (
      <div className="divide-y divide-gray-100">
        {DIAS_LARGO.map((nombre, k) => {
          const d = k + 1, h = H[d];
          return (
            <div key={d} className="flex items-center gap-3 py-2 flex-wrap">
              <Switch on={h.activo} label={nombre} onChange={(v) => set((o) => { o.horarios[d].activo = v; })} />
              <span className={`text-sm w-24 ${h.activo ? 'text-gray-800' : 'text-gray-400'}`}>{nombre}</span>
              <div className="flex items-center gap-2">
                <Hora label={`Entrada ${nombre}`} disabled={!h.activo} value={h.inicio} onChange={(v) => cambiar([d], 'inicio', v)} />
                <span className="text-gray-400">a</span>
                <Hora label={`Salida ${nombre}`} disabled={!h.activo} value={h.fin} onChange={(v) => cambiar([d], 'fin', v)} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      <div className="inline-flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1 mt-1">
        {MODOS.map(([k, n]) => (
          <button key={k} type="button" onClick={() => set((o) => { o.horarioModo = k; })}
            className={`text-xs sm:text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${modo === k ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
            {n}
          </button>
        ))}
      </div>
      {cuerpo}
      <p className="text-xs text-gray-500 mt-1">Los avisos solo cuentan los días que se trabaja y comparan contra este horario.</p>
    </div>
  );
}

// ---------- vista previa ----------
function VistaPrevia({ datos, onCerrar }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onCerrar}>
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900">{datos.titulo || 'Vista previa con los datos de hoy'}</p>
            <p className="text-xs text-gray-500 mt-0.5">{datos.subtitulo || 'Orientativa: no se envía nada. Usa lo que detectó la cámara en los últimos 50 días y la configuración de esta pantalla, aunque todavía no la hayas guardado.'}</p>
          </div>
          <button type="button" onClick={onCerrar} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" aria-label="Cerrar"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto px-5 py-3 divide-y divide-gray-100">
          {datos.resultados.map((r) => {
            const e = r.key === 'reporte' && r.estado === 'ok' ? { ...ESTADO_PREVIA.ok, txt: 'Activo' } : ESTADO_PREVIA[r.estado] || ESTADO_PREVIA.sin_datos;
            return (
              <div key={r.key} className="py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-800">{r.titulo}</p>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${e.cls}`}>{e.txt}</span>
                </div>
                <p className="text-xs text-gray-600 mt-0.5">{r.texto}</p>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-500">
          {datos.pie || 'Si un aviso ya salió en los últimos días, el bot no lo repite aunque acá diga «Hoy avisaría».'}
        </div>
      </div>
    </div>
  );
}


// ---------- qué avisos hay (lo primero que se ve: sin pedir teléfono ni mail) ----------
// "Cámara sin imágenes" no está acá: vive solo en la tarjeta 📷 Cámara. Entregas: solo en 🚚 Entregas.
const AVISOS_OBRA = [
  { key: 'inactividad', emoji: '🛑', titulo: 'Obra parada', linea: 'Varios días hábiles seguidos sin gente trabajando.', campo: 'dias', opciones: [3, 7, 15, 30], fmt: (v) => `Si pasan ${v} días` },
  { key: 'horario_bajo', emoji: '⏱️', titulo: 'Pocas horas', linea: 'La semana viene con menos horas de trabajo de lo normal.', campo: 'horas', opciones: [4, 5, 6, 7, 8], fmt: (v) => `Si hay menos de ${v} h` },
  { key: 'arranque_tardio', emoji: '🌅', titulo: 'Arrancan tarde', linea: 'La primera persona llega después de la hora de entrada.', campo: 'tolerancia', opciones: [15, 30, 45, 60], fmt: (v) => `Si llegan ${v} min tarde` },
  { key: 'caida_dotacion', emoji: '👷', titulo: 'Menos gente', linea: 'Trabajó bastante menos gente que lo habitual.', campo: 'pct', opciones: [40, 50, 60, 70], fmt: (v) => `Si va menos del ${v} %` },
  { key: 'epp', emoji: '⛑️', titulo: 'Sin casco', linea: 'Varias personas trabajando sin casco.', nota: 'Orientativo: puede marcar gente con casco si está lejos o de espaldas.' },
  { key: 'reporte', emoji: '📋', titulo: 'Resumen del día', linea: 'A qué hora arrancó y terminó la obra y cuántas horas se trabajó.' },
];
// Avisos de clima (obra.alertas.clima). Los evalúa el motor del Editor con el pronóstico de Open-Meteo.
export const AVISOS_CLIMA = [
  { key: 'tormenta', emoji: '⛈️', titulo: 'Tormenta eléctrica', linea: 'Apenas el pronóstico marque tormenta en las próximas 3 h. Es urgente: puede llegar fuera del horario de 8 a 20 h.' },
  { key: 'lluvia_manana', emoji: '🌧️', titulo: 'Mañana llueve', linea: 'A la tarde, si mañana va a llover en el horario de obra.', campos: [{ campo: 'mm', opciones: [1, 2, 5, 10], fmt: (v) => `Desde ${v} mm` }] },
  { key: 'lluvia_fuerte', emoji: '🌊', titulo: 'Lluvia fuerte en las próximas horas', linea: 'Para cubrir material y proteger lo recién hecho.', campos: [{ campo: 'horas', opciones: [1, 2, 3, 6], fmt: (v) => `En las próximas ${v} h` }, { campo: 'mm', opciones: [2, 4, 8, 15], fmt: (v) => `${v} mm por hora o más` }] },
  { key: 'lluvia_ahora', emoji: '☔', titulo: 'Está lloviendo en la obra', linea: 'Lluvia medida casi en tiempo real (cada 15 min) en la ubicación de la obra.' },
  { key: 'viento', emoji: '💨', titulo: 'Viento fuerte (grúa)', linea: 'Ráfagas fuertes en las próximas 6 h de trabajo: reprogramar izajes.', campos: [{ campo: 'kmh', opciones: [30, 40, 50, 60], fmt: (v) => `Ráfagas de ${v} km/h o más` }] },
  { key: 'temperatura', emoji: '🌡️', titulo: 'Calor o frío extremo', linea: 'A la tarde, si mañana hace mucho calor o hay helada.', campos: [{ campo: 'max', opciones: [32, 35, 38], fmt: (v) => `Calor desde ${v}°` }, { campo: 'min', opciones: [-2, 0, 3], fmt: (v) => `Frío desde ${v}°` }] },
  { key: 'hormigonado', emoji: '🚛', titulo: 'Día ideal para hormigonar', linea: 'A la tarde, si mañana es un buen día para llenar (puntaje 80 o más).' },
  { key: 'resumen_semanal', emoji: '📅', titulo: 'El clima de la semana', linea: 'Los lunes: qué días se puede trabajar en la semana.', campos: [{ campo: 'hora', opciones: ['08:00', '09:00', '10:00', '12:00'], fmt: (v) => `Lunes a las ${v} h` }] },
  { key: 'camara_lluvia', emoji: '🧪', titulo: 'Lluvia vista por la cámara', linea: 'Suma la última foto de la cámara (lente mojada o imagen borrosa) para confirmar que llueve.', nota: 'Beta: puede confundirse con niebla, la noche o la lente sucia. Solo cuenta si el servicio meteorológico también marca lluvia.', beta: true },
];
const climaDe = (o) => {
  const c = (o.alertas && o.alertas.clima) || {};
  const out = {};
  Object.keys(R.AVISOS_DEFAULT).forEach((k) => { out[k] = { ...R.AVISOS_DEFAULT[k], ...(c[k] || {}) }; });
  return out;
};

const HORAS_REPORTE = Array.from({ length: 12 }, (_, i) => `${String(8 + i).padStart(2, '0')}:00`);
const emailOk = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());

function TarjetaAviso({ def, obra, set, clima }) {
  const esReporte = def.key === 'reporte';
  const valor = clima ? obra.alertas.clima[def.key] : esReporte ? obra.reporte : obra.alertas[def.key];
  const on = !!valor?.activo && (clima || def.key !== 'epp' || obra.casco);
  const cambiar = (activo) => set((o) => {
    if (clima) o.alertas.clima[def.key].activo = activo;
    else if (esReporte) o.reporte.activo = activo;
    else o.alertas[def.key].activo = activo;
    if (!clima && def.key === 'epp' && activo) o.casco = true; // prender "sin casco" = la obra exige casco
  });
  // un aviso puede tener uno o dos parámetros (ej. lluvia fuerte: horas y mm)
  const campos = def.campos || (def.opciones ? [{ campo: def.campo, opciones: def.opciones, fmt: def.fmt }] : []);
  const opcionesDe = (c) => Array.from(new Set([...c.opciones, valor?.[c.campo]].filter((x) => x != null))).sort((x, y) => (typeof x === 'number' ? x - y : String(x).localeCompare(String(y))));
  const setCampo = (c, v) => set((o) => {
    const t = clima ? o.alertas.clima[def.key] : o.alertas[def.key];
    t[c.campo] = typeof c.opciones[0] === 'number' ? Number(v) : v;
  });
  return (
    <div className={`rounded-2xl border-2 p-3 sm:p-4 transition-colors flex flex-col gap-2 ${on ? 'border-blue-200 bg-white shadow-sm' : 'border-gray-200 bg-white/70'}`} data-aviso={def.key}>
      <div className="flex items-start gap-3">
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0 ${on ? 'bg-blue-50' : 'bg-gray-100 grayscale-[40%]'}`} aria-hidden="true">{def.emoji}</span>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${on ? 'text-gray-900' : 'text-gray-700'}`}>{def.titulo}{def.beta && <span className="ml-1.5 align-middle text-[10px] font-bold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-1.5 py-0.5">Beta</span>}</p>
          <p className="text-xs text-gray-500 leading-snug">{def.linea}</p>
          {def.nota && <p className="text-[11px] text-amber-700 mt-1 leading-snug">{def.nota}</p>}
        </div>
        <Switch on={on} label={def.titulo} onChange={cambiar} />
      </div>
      {on && campos.length > 0 && (
        <div className={`grid gap-2 ${campos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {campos.map((c) => (
            <select key={c.campo} value={valor[c.campo]} onChange={(e) => setCampo(c, e.target.value)} className={`${inputCls} w-full text-sm`} aria-label={`Cuándo avisar: ${def.titulo}`}>
              {opcionesDe(c).map((x) => <option key={x} value={x}>{c.fmt(x)}</option>)}
            </select>
          ))}
        </div>
      )}
      {on && esReporte && (
        <select value={obra.reporte.hora} onChange={(e) => set((o) => { o.reporte.hora = e.target.value; })} className={`${inputCls} w-full text-sm`} aria-label="Hora del resumen">
          {Array.from(new Set([...HORAS_REPORTE, obra.reporte.hora])).sort().map((h) => <option key={h} value={h}>A las {h} h</option>)}
        </select>
      )}
    </div>
  );
}

// ---------- ¿a quién le aviso? ----------
function Destinatario({ d, onChange, onQuitar, onPausa, ocupado, error }) {
  const nuevo = !!d.nuevo;
  const editable = nuevo || d.canalesEditables;
  return (
    <div className={`rounded-xl border p-3 ${error ? 'border-amber-300 bg-amber-50/60' : 'border-gray-200 bg-white'} ${!nuevo && !d.habilitada ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-lg" aria-hidden="true">📱</span>
        {nuevo ? (
          <input type="tel" inputMode="tel" autoComplete="tel" value={d.phone} onChange={(e) => onChange({ phone: e.target.value })} placeholder="54 9 11 2345-6789" className={`${inputCls} flex-1 basis-[calc(100%-2.5rem)] sm:basis-auto min-w-[10rem]`} aria-label="Número de WhatsApp" />
        ) : (
          <span className="text-sm font-semibold text-gray-800 flex-1 basis-[calc(100%-2.5rem)] sm:basis-auto min-w-0 truncate">{formatearTelefono(d.phone)}{!d.habilitada && <span className="ml-2 text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">En pausa</span>}</span>
        )}
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" className="accent-blue-600" disabled={!editable} checked={d.notifWsp} onChange={(e) => onChange({ notifWsp: e.target.checked })} />💬 WhatsApp</label>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" className="accent-blue-600" disabled={!editable} checked={d.notifEmail} onChange={(e) => onChange({ notifEmail: e.target.checked })} />✉️ Mail</label>
        {!nuevo && (
          <button type="button" disabled={ocupado} onClick={onPausa} title={d.habilitada ? 'Pausar los avisos de esta obra para este número' : 'Reanudar'} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100">{d.habilitada ? <PauseCircle size={16} /> : <PlayCircle size={16} />}</button>
        )}
        <button type="button" disabled={ocupado} onClick={onQuitar} title="Quitar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
      </div>
      {d.notifEmail && (
        <input type="email" disabled={!editable} value={d.email || ''} onChange={(e) => onChange({ email: e.target.value })} placeholder="nombre@empresa.com" autoComplete="email" className={`${inputCls} w-full mt-2`} aria-label="Correo para los avisos" />
      )}
      {!editable && <p className="text-[11px] text-gray-500 mt-1">Este número también recibe avisos de otras obras: los canales los cambia el titular o Soporte.</p>}
      {error && <p className="text-xs text-amber-800 mt-1.5">{error}</p>}
    </div>
  );
}

// ---------- sección completa ----------
const AlertasObra = ({ feedID, telefonoSugerido, modo = 'obra', probar, notaClima }) => {
  const clima = modo === 'clima';
  const AVISOS = clima ? AVISOS_CLIMA : AVISOS_OBRA;
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [obra, setObra] = useState(null); // selección de avisos (la misma para todos los números de esta obra)
  const [dest, setDest] = useState([]);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [ajustes, setAjustes] = useState(false);
  const [base, setBase] = useState('');
  const bloqueDest = useRef(null);

  const sugerido = useMemo(() => {
    if (telefonoSugerido) return soloDigitos(telefonoSugerido);
    const raw = getAuthClaims().raw || {};
    return soloDigitos(raw.phoneNumber || raw.phone || '');
  }, [telefonoSugerido]);

  const aplicar = useCallback((r) => {
    setDatos(r);
    const o = copia((r.configs[0] && r.configs[0].obra) || r.defaults);
    o.alertas = o.alertas || {};
    o.alertas.clima = climaDe(o); // backend sin avisos de clima todavía: se completan con los defaults
    setObra(o);
    const ds = r.configs.map((c) => ({ phone: c.phone, notifWsp: c.notifWsp, notifEmail: c.notifEmail, email: c.email, canalesEditables: c.canalesEditables, habilitada: c.obra?.habilitada !== false, activo: c.activo }));
    setDest(ds);
    setBase(JSON.stringify({ o, ds }));
    setErrores({});
  }, []);
  const cargar = useCallback(() => {
    if (!feedID) return;
    setError(null);
    alertasApi.listar(feedID).then(aplicar).catch((e) => setError(errorMsg(e)));
  }, [feedID, aplicar]);
  useEffect(() => { cargar(); }, [cargar]);

  if (error) {
    return (
      <div className="text-sm text-gray-700 bg-red-50 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <span>No pudimos cargar las alertas: {error}</span>
        <button type="button" onClick={cargar} className="text-sm font-semibold text-red-700 hover:underline">Reintentar</button>
      </div>
    );
  }
  if (!datos || !obra) return <div className="flex items-center gap-2 text-sm text-gray-500 py-6"><Loader2 size={16} className="animate-spin" />Cargando alertas…</div>;

  const set = (fn) => setObra((o) => { const n = copia(o); fn(n); if (!n.casco) n.alertas.epp.activo = false; return n; });
  const sucio = JSON.stringify({ o: obra, ds: dest }) !== base;
  const activos = clima
    ? AVISOS_CLIMA.filter((a) => obra.alertas.clima[a.key]?.activo)
    : AVISOS_OBRA.filter((a) => (a.key === 'reporte' ? obra.reporte.activo : obra.alertas[a.key]?.activo && (a.key !== 'epp' || obra.casco)));
  const cambiarDest = (i, patch) => { setDest((ds) => ds.map((d, j) => (j === i ? { ...d, ...patch } : d))); setErrores((e) => ({ ...e, [i]: null })); };
  const sumarDest = () => setDest((ds) => [...ds, { nuevo: true, phone: ds.some((d) => d.phone === sugerido) ? '' : sugerido, notifWsp: true, notifEmail: false, email: '' }]);

  const irADestinatarios = () => bloqueDest.current && bloqueDest.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

  // Lo que se manda: la selección sin "cámara sin imágenes" ni entregas (esas se configuran en sus tarjetas y el
  // backend conserva lo que cada número ya tenía).
  const obraParaGuardar = () => {
    // Cada pantalla manda solo lo suyo: así guardar los avisos de clima no pisa los de la obra (ni al revés)
    // aunque las dos estén abiertas. El backend conserva lo que no llega.
    if (clima) return { alertas: { clima: copia(obra.alertas.clima) } };
    const o = copia(obra);
    delete o.alertas.camara_ciega;
    delete o.alertas.entregas;
    delete o.alertas.clima;
    delete o.habilitada;
    return o;
  };

  const guardar = async () => {
    // Recién acá se validan los destinatarios, con un mensaje que lleva al bloque
    const errs = {};
    dest.forEach((d, i) => {
      const dig = soloDigitos(d.phone);
      if (d.nuevo && (dig.length < 10 || dig.length > 15)) errs[i] = 'Falta el número completo, con código de país y de área (ej. 54 9 11 2345-6789).';
      else if (!d.notifWsp && !d.notifEmail) errs[i] = 'Elegí WhatsApp, mail o los dos.';
      else if (d.notifEmail && !emailOk(d.email)) errs[i] = 'Para avisar por mail falta un correo válido (o destildá ✉️ Mail).';
    });
    if (activos.length && !dest.length) {
      toast.info('Ya elegiste los avisos 👌 Ahora decinos a quién le avisamos en «¿A quién le aviso?».');
      irADestinatarios();
      return;
    }
    if (Object.keys(errs).length) {
      setErrores(errs);
      toast.info('Revisá «¿A quién le aviso?»: falta completar un dato.');
      irADestinatarios();
      return;
    }
    setGuardando(true);
    try {
      const body = obraParaGuardar();
      let ultimo = null;
      for (const d of dest) {
        const phone = soloDigitos(d.phone);
        if (d.nuevo) await alertasApi.sumar({ phone, feedId: feedID, notifWsp: d.notifWsp, notifEmail: d.notifEmail, email: (d.email || '').trim() });
        const canales = d.nuevo || d.canalesEditables ? { notifWsp: d.notifWsp, notifEmail: d.notifEmail, email: (d.email || '').trim() } : {};
        ultimo = await alertasApi.guardar(phone, feedID, { obra: body, ...canales });
      }
      if (ultimo) toast.success(clima ? '¡Listo! Los avisos de clima quedaron guardados.' : '¡Listo! Las alertas de la obra quedaron guardadas.');
      aplicar(await alertasApi.listar(feedID));
    } catch (e) { toast.error(errorMsg(e)); }
    setGuardando(false);
  };

  const quitar = async (i) => {
    const d = dest[i];
    if (d.nuevo) { setDest((ds) => ds.filter((_, j) => j !== i)); return; }
    if (!window.confirm(`¿Dejar de mandar avisos de esta obra al ${formatearTelefono(d.phone)}?`)) return;
    setOcupado(true);
    try { await alertasApi.quitar(d.phone, feedID); toast.success('Listo: ese número ya no recibe avisos de esta obra.'); aplicar(await alertasApi.listar(feedID)); } catch (e) { toast.error(errorMsg(e)); }
    setOcupado(false);
  };
  const pausa = async (i) => {
    const d = dest[i];
    setOcupado(true);
    try { await alertasApi.pausar(d.phone, feedID, { habilitada: !d.habilitada }); cambiarDest(i, { habilitada: !d.habilitada }); setBase((b) => { const x = JSON.parse(b); x.ds[i].habilitada = !d.habilitada; return JSON.stringify(x); }); toast.success(d.habilitada ? 'En pausa para este número.' : 'Avisos reanudados.'); } catch (e) { toast.error(errorMsg(e)); }
    setOcupado(false);
  };
  const verPrevia = async () => {
    setOcupado(true);
    try { setPrevia(clima && probar ? await probar(obraParaGuardar()) : await alertasApi.vistaPrevia(dest.find((d) => !d.nuevo)?.phone || '0', feedID, obraParaGuardar())); } catch (e) { toast.error(errorMsg(e)); }
    setOcupado(false);
  };

  return (
    <div className="space-y-4" data-alertas-obra>
      {/* 1. Qué avisos */}
      <section>
        <div className="flex items-end justify-between gap-2 mb-2 px-1">
          <h4 className="text-sm font-semibold text-gray-800">¿De qué te aviso?</h4>
          <span className="text-xs text-gray-500">{activos.length ? `${activos.length} de ${AVISOS.length} prendidos` : 'Prendé los que te sirvan'}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {AVISOS.map((a) => <TarjetaAviso key={a.key} def={a} obra={obra} set={set} clima={clima} />)}
        </div>
        {clima && notaClima}
        {!clima && <button type="button" onClick={() => setAjustes(!ajustes)} className="mt-2 px-1 inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline" aria-expanded={ajustes}>
          <ChevronDown size={14} className={`transition-transform ${ajustes ? 'rotate-180' : ''}`} />Horario de trabajo y gente habitual ({obra.dotacion} personas)
        </button>}
        {!clima && ajustes && (
          <div className="mt-2 rounded-2xl border border-gray-200 bg-white p-3 sm:p-4 space-y-3">
            <Horario obra={obra} set={set} />
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
              <div><p className="text-sm font-medium text-gray-800">Gente en un día normal</p><p className="text-xs text-gray-500">Para el aviso de «Menos gente»</p></div>
              <input type="number" min={1} max={500} value={obra.dotacion} aria-label="Dotación habitual" onChange={(e) => set((o) => { o.dotacion = Math.max(1, Math.min(500, Number(e.target.value) || 1)); })} className={`${inputCls} w-20 text-right`} />
            </div>
            {obra.reporte.activo && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1.5">Días que llega el resumen</p>
                <Dias activos={obra.reporte.dias} onToggle={(d) => set((o) => { const x = o.reporte.dias.indexOf(d); if (x < 0) o.reporte.dias.push(d); else o.reporte.dias.splice(x, 1); o.reporte.dias.sort(); })} />
              </div>
            )}
          </div>
        )}
      </section>

      {/* 2. A quién */}
      <section ref={bloqueDest} className="rounded-2xl border border-gray-200 bg-white p-3 sm:p-4" data-destinatarios>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div>
            <h4 className="text-sm font-semibold text-gray-800">¿A quién le aviso?</h4>
            <p className="text-xs text-gray-500">Por WhatsApp, por mail o los dos. Los avisos salen entre las 8 y las 20 h.</p>
          </div>
        </div>
        <div className="space-y-2">
          {dest.map((d, i) => (
            <Destinatario key={d.nuevo ? `n${i}` : d.phone} d={d} error={errores[i]} ocupado={ocupado} onChange={(p) => cambiarDest(i, p)} onQuitar={() => quitar(i)} onPausa={() => pausa(i)} />
          ))}
          {!dest.length && <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2.5">Todavía nadie recibe los avisos de esta obra.</p>}
        </div>
        <button type="button" onClick={sumarDest} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"><Plus size={16} />Sumar un número</button>
      </section>

      {/* 3. Guardar */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2">
        <button type="button" disabled={ocupado} onClick={verPrevia} className="sm:mr-auto inline-flex items-center justify-center gap-1.5 text-sm font-semibold border border-gray-300 bg-white rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50">
          <Eye size={15} />{clima ? 'Probar con el pronóstico de hoy' : 'Probar con los datos de hoy'}
        </button>
        <button type="button" disabled={!sucio || guardando} onClick={guardar} className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-lg px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
          {guardando && <Loader2 size={15} className="animate-spin" />}{sucio ? 'Guardar alertas' : 'Guardado'}
        </button>
      </div>
      {previa && <VistaPrevia datos={previa} onCerrar={() => setPrevia(null)} />}
    </div>
  );
};

export default AlertasObra;
